import type { PendingApprovalsView } from "@/schemas/team";

/** My Team's nav count: the manager's own (pending requests plus cancel asks). */
export function teamNavCount(view: PendingApprovalsView | undefined): number {
  return view?.teamCount ?? 0;
}

/**
 * PTO's nav count: HR-routed requests and cancel asks waiting on the HR approver (`hrCount`,
 * already 0 for everyone else). Read from the same view as My Team's, so the shell makes one count
 * request and the two pills never disagree.
 */
export function ptoNavCount(view: PendingApprovalsView | undefined): number {
  return view?.hrCount ?? 0;
}

const TEAM_HREF = "/dashboard/team";
const PTO_HREF = "/dashboard/pto";

/**
 * The Home banner: its count and where Review goes, or null when there is nothing to show.
 * Every count includes cancel asks as well as pending requests (summed server-side by
 * `actionableCounts`), so a cancel ask alone raises the banner.
 * Until the PTO page is built only `teamCount` counts and Review goes to My Team (a pure HR
 * approver manages no one and would be redirected, so they see no banner yet). Once it is
 * built the banner uses the full `count` and Review follows `target`: HR items open PTO's
 * Approvals tab (`?tab=approvals`). My Team opens with the PTO
 * requests dialog already up (`?pto=open`).
 */
export function bannerFor(
  view: PendingApprovalsView | undefined,
  ptoBuilt: boolean,
): { count: number; href: string; search: { pto?: "open"; tab?: "approvals" } } | null {
  if (!view) return null;
  const count = ptoBuilt ? view.count : view.teamCount;
  if (count <= 0) return null;
  return ptoBuilt && view.target === "pto"
    ? { count, href: PTO_HREF, search: { tab: "approvals" } }
    : { count, href: TEAM_HREF, search: { pto: "open" } };
}
